import { PipelineStepType, StepCatalogEntry } from './pipelineBuilder.types'

export const PIPELINE_STEP_ORDER: PipelineStepType[] = [
    'prospects',
    'leads',
    'tours',
    'application',
    'screening',
    'qualification',
    'lease',
    'guarantor',
    'future_resident',
    'unit_assignment',
]

export const STEP_CATALOG: Record<PipelineStepType, StepCatalogEntry> = {
    prospects: {
        type: 'prospects',
        label: 'Prospects',
        description:
            'Pool audience for the season. Campaign auto-enrollment for prospects with contact info.',
        required: true,
        entryOptions: [
            { value: 'auto', label: 'Pool / served schools audience' },
        ],
        completionOptions: [
            { value: 'contact_enriched', label: 'Contact enriched' },
            { value: 'enrolled_in_campaign', label: 'Enrolled in campaign' },
            { value: 'pm_advances', label: 'PM advances manually' },
        ],
        defaultEntry: { mode: 'auto' },
        defaultCompletion: { condition: 'contact_enriched' },
        defaultOnComplete: { action: 'hold' },
        builderHint:
            'Campaign enrollment settings. Exit to Leads is configured on the Prospects → Leads transition.',
    },
    leads: {
        type: 'leads',
        label: 'Leads',
        description:
            'Entry roster when someone becomes a lead via campaign, guest card, or call.',
        required: true,
        entryOptions: [
            { value: 'on_event:lead_created', label: 'When lead is created' },
        ],
        completionOptions: [
            { value: 'lead_created', label: 'Lead is created' },
            { value: 'account_created', label: 'Applicant account created' },
            { value: 'pm_advances', label: 'PM advances manually' },
        ],
        defaultEntry: { mode: 'on_event', trigger: 'lead_created' },
        defaultCompletion: { condition: 'account_created' },
        defaultOnComplete: { action: 'manual' },
        builderHint:
            'Configure on-enter automations and portal collection here. Exit conditions are on the outgoing transition.',
    },
    tours: {
        type: 'tours',
        label: 'Tours',
        description: 'Schedule and track property tours.',
        required: false,
        entryOptions: [
            { value: 'auto', label: 'Automatically when prior step completes' },
            { value: 'manual', label: 'Manually by property manager' },
        ],
        completionOptions: [
            { value: 'tour_scheduled', label: 'Tour is scheduled' },
            { value: 'tour_completed', label: 'Tour is completed' },
            { value: 'tour_skipped', label: 'Tour skipped by PM' },
            { value: 'tour_no_show', label: 'Tour marked no-show' },
        ],
        defaultEntry: { mode: 'manual' },
        defaultCompletion: { condition: 'tour_completed' },
        defaultOnComplete: { action: 'auto_advance' },
        builderHint:
            'Tour after Application is unusual — applicant may already be committed.',
    },
    application: {
        type: 'application',
        label: 'Application',
        description:
            'Send and collect signed rental application via Signable Docs.',
        required: false,
        entryOptions: [
            { value: 'manual', label: 'Manually by property manager' },
            { value: 'auto', label: 'Automatically when prior step completes' },
        ],
        completionOptions: [
            { value: 'sent', label: 'Application is sent' },
            { value: 'all_signatures', label: 'All signatures received' },
            {
                value: 'all_signatures_and_fee_paid',
                label: 'All signatures received and fee paid',
            },
        ],
        defaultEntry: { mode: 'manual' },
        defaultCompletion: { condition: 'all_signatures' },
        defaultOnComplete: {
            action: 'require_review',
            review_prompt: 'Review signed application before continuing',
        },
    },
    screening: {
        type: 'screening',
        label: 'Screening',
        description: 'Credit, background, and income verification.',
        required: false,
        entryOptions: [
            { value: 'auto', label: 'Automatically when prior step completes' },
            { value: 'manual', label: 'Manually by property manager' },
        ],
        completionOptions: [
            { value: 'report_received', label: 'Screening report received' },
            {
                value: 'report_received_and_scored',
                label: 'Report received and scored',
            },
        ],
        defaultEntry: { mode: 'auto' },
        defaultCompletion: { condition: 'report_received_and_scored' },
        defaultOnComplete: { action: 'auto_advance' },
        builderHint:
            'One Screening phase — credit/background/income are checks in config. Decisioning is Qualification.',
    },
    qualification: {
        type: 'qualification',
        label: 'Qualification',
        description:
            'Post-screening decisioning: thresholds, self-qualify, and conditional methods.',
        required: false,
        entryOptions: [
            { value: 'auto', label: 'Automatically when prior step completes' },
            { value: 'manual', label: 'Manually by property manager' },
        ],
        completionOptions: [
            { value: 'self_qualified', label: 'Self-qualified' },
            {
                value: 'conditional_resolved',
                label: 'Conditional method resolved',
            },
            { value: 'declined', label: 'Declined' },
            { value: 'pm_advances', label: 'PM advances manually' },
        ],
        defaultEntry: { mode: 'auto' },
        defaultCompletion: { condition: 'conditional_resolved' },
        defaultOnComplete: { action: 'auto_advance' },
        builderHint:
            'Always follows Screening. Guarantor / Leap / prepay are outcomes here, not separate timeline rows.',
    },
    lease: {
        type: 'lease',
        label: 'Lease',
        description: 'Send and collect signed lease agreement.',
        required: false,
        entryOptions: [
            { value: 'manual', label: 'Manually by property manager' },
            { value: 'auto', label: 'Automatically when prior step completes' },
        ],
        completionOptions: [
            { value: 'sent', label: 'Lease is sent' },
            { value: 'all_signatures', label: 'All parties signed' },
        ],
        defaultEntry: { mode: 'manual' },
        defaultCompletion: { condition: 'all_signatures' },
        defaultOnComplete: { action: 'auto_advance' },
    },
    guarantor: {
        type: 'guarantor',
        label: 'Guarantor',
        description: 'Collect guarantor application or waiver when needed.',
        required: false,
        entryOptions: [
            { value: 'auto', label: 'Automatically when prior step completes' },
            { value: 'manual', label: 'Manually by property manager' },
        ],
        completionOptions: [
            { value: 'guarantor_invited', label: 'Guarantor invited' },
            { value: 'guarantor_submitted', label: 'Guarantor submitted' },
            { value: 'guarantor_approved', label: 'Guarantor approved' },
            { value: 'waiver_signed', label: 'Waiver signed' },
        ],
        defaultEntry: { mode: 'auto' },
        defaultCompletion: { condition: 'guarantor_approved' },
        defaultOnComplete: { action: 'auto_advance' },
        builderHint:
            'Guarantor after Lease Signed without Screening is unusual — guarantor decisions usually follow screening.',
    },
    future_resident: {
        type: 'future_resident',
        label: 'Future Resident',
        description: 'Post-lease checklist until move-in.',
        required: false,
        entryOptions: [
            { value: 'auto', label: 'Automatically when prior step completes' },
        ],
        completionOptions: [
            { value: 'checklist_complete', label: 'Checklist complete' },
            { value: 'pm_marks_complete', label: 'PM marks complete' },
        ],
        defaultEntry: { mode: 'auto' },
        defaultCompletion: { condition: 'checklist_complete' },
        defaultOnComplete: { action: 'auto_advance' },
    },
    unit_assignment: {
        type: 'unit_assignment',
        label: 'Unit Assignment',
        description: 'Assign the resident to a specific unit / bed.',
        required: false,
        entryOptions: [
            { value: 'auto', label: 'Automatically when prior step completes' },
            { value: 'manual', label: 'PM starts unit assignment' },
        ],
        completionOptions: [
            { value: 'unit_assigned', label: 'Unit assigned' },
            { value: 'pm_marks_complete', label: 'PM marks complete' },
        ],
        defaultEntry: { mode: 'manual' },
        defaultCompletion: { condition: 'unit_assigned' },
        defaultOnComplete: { action: 'hold' },
    },
}

export function getCatalogEntry(type: PipelineStepType): StepCatalogEntry {
    return STEP_CATALOG[type]
}

export function entryModeFromValue(
    value: string,
): {
    mode: 'auto' | 'manual' | 'on_event'
    trigger?: string
} {
    if (value.startsWith('on_event:')) {
        return { mode: 'on_event', trigger: value.replace('on_event:', '') }
    }
    return { mode: value as 'auto' | 'manual' }
}

export function entryValueFromMode(entry: {
    mode: string
    trigger?: string
}): string {
    if (entry.mode === 'on_event' && entry.trigger) {
        return `on_event:${entry.trigger}`
    }
    return entry.mode
}
